// Представление «Заказчики». Строка: название, отрасль, регион, контактное лицо.
//
// Заказчик — это верх иерархии «юрлицо → заказчик → договор → объект → пост», и открывают
// его ради одного вопроса: какие у него объекты. Поэтому деталь заказчика ведёт в реестр
// объектов с уже наложенным отбором, а не пересказывает его карточку второй раз.

import type { ReactNode } from 'react';

import { RiAddLine, RiBriefcaseLine } from '@remixicon/react';
import { useNavigate } from 'react-router';

import { ListPane } from '@/layout/ListPane';
import type { SearchState } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { parseMoment } from '@/shared/time/format';
import * as Button from '@/shared/ui/button';
import { EmptyState, ScreenState } from '@/shared/ui/screen-state';

import { ListRow, ListSkeleton } from '@/shared/ui/list-row';
import { SearchFilter } from '@/shared/ui/search-filter';
import type { SitesRoute } from '../route';
import { openNewCustomerHref, sitesHref } from '../route';
import { useCustomersQuery } from '@/shared/org/queries';

const CUSTOMER_FORMS = ['заказчик', 'заказчика', 'заказчиков'] as const;

export function CustomersList({
  route,
  viewSwitcher,
}: {
  route: SitesRoute;
  viewSwitcher: ReactNode;
}) {
  const navigate = useNavigate();
  const { search, rowId } = route;
  const query = useCustomersQuery({ q: search.q });

  function apply(next: SearchState) {
    navigate(sitesHref('customers', next, rowId), { replace: true });
  }

  return (
    <ListPane
      caption={
        query.data === undefined ? 'Заказчики' : countLabel(query.data.total, CUSTOMER_FORMS)
      }
      sliceAt={parseMoment(query.data?.generated_at)}
      viewSwitcher={viewSwitcher}
      search={
        <SearchFilter
          label='Поиск заказчика по названию'
          placeholder='Название заказчика'
          value={search.q ?? ''}
          onApply={(value) => apply({ ...search, q: value === '' ? undefined : value })}
        />
      }
      note={
        /* Заведение стоит рядом с поиском, а не только в пустом состоянии: заказчик появляется
           не один раз за жизнь системы, и после первого список уже не пуст. Отбором оно при
           этом не является и вместе с отборами не прячется. */
        <Button.Root
          variant='primary'
          mode='stroke'
          size='xsmall'
          onClick={() => navigate(openNewCustomerHref(route))}
        >
          <Button.Icon as={RiAddLine} />
          Новый заказчик
        </Button.Root>
      }
    >
      <ScreenState
        data={query.data}
        error={query.error}
        subject='список заказчиков'
        isEmpty={(data) => data.items.length === 0}
        onRetry={() => void query.refetch()}
        loading={<ListSkeleton />}
        empty={
          <EmptyState
            icon={RiBriefcaseLine}
            title={search.q === undefined ? 'Заказчиков пока нет' : 'Под этот поиск заказчиков нет'}
            description={
              search.q === undefined
                ? 'Ни один заказчик ещё не заведён. Заказчик нужен объекту при создании: без него объект завести не выйдет. Кнопка «Новый заказчик» — над списком.'
                : 'Список пуст из-за поиска, а не потому, что заказчиков нет вовсе. Очистите поле поиска — увидите весь список.'
            }
          />
        }
      >
        {(data) => (
          <div className='flex flex-col'>
            {data.items.map((customer) => (
              <ListRow
                key={customer.id}
                to={sitesHref('customers', search, customer.id)}
                selected={customer.id === rowId}
                title={customer.name}
                // Контактное лицо называется, ТОЛЬКО если оно есть. Пока в строке стояло
                // «контакт не указан», эта фраза повторялась во всех четырнадцати строках
                // подряд и не сообщала ничего: отсутствие одного и того же у всех — не факт
                // о строке. В карточке причина по-прежнему названа словами.
                subtitle={[
                  customer.industry ?? 'отрасль не указана',
                  customer.region ?? 'регион не указан',
                  ...(customer.contact_person === null ? [] : [customer.contact_person]),
                ].join(' · ')}
              />
            ))}
          </div>
        )}
      </ScreenState>
    </ListPane>
  );
}
